import { isEmpty } from "lodash";

import * as MosaicManagerActions from "../actions/Mosaic.Actions";
import { IMosaicNode, ILeafNode, IParentNode } from "src/utils/MosaicTree";
import { isCustomerViewId } from "src/features/featureRegistry";
import { v1 } from "uuid";

// Embedded Pages
import DashboardWindow from "src/pages/DashboardWindow";

// TESTING
import { BetaEventEditor } from "src/pages/TimelineEventEditors/BetaEventEditor";

import { MovementMessages } from "src/pages/MovementMessages";
import { FlightBuilder } from "src/pages/FlightBuilder/FlightBuilder";
import { FlightTimelineWrapper } from "src/pages/FlightTimeline/FlightTimelineWrapper";
import { AdvancedSearch } from "src/pages/AdvancedSearch/AdvancedSearch";
import FlightSummary from "src/pages/FlightSummary/FlightSummary";
import { AdminWindow } from "src/pages/Admin/AdminWindow";
import { CrewScheduleWindow } from "src/pages/CrewSchedule/CrewScheduleWindow";
import { PairingEditorWindow } from "src/components/CrewSchedule/PairingEditorWindow/PairingEditorWindow";
import { ImportEventsWindow } from "src/pages/ImportEvents/ImportEventsWindow";
import { NFAEditorWindow } from "src/components/CrewSchedule/NFAEditorWindow";
import { ManageContentWindow } from "src/pages/ContentManagement/ManageContentWindow";
import { ReviewContentWindow } from "src/pages/ContentManagement/ReviewContentWindow";
import { ContentListWindow } from "src/pages/ContentManagement/ContentListWindow";
import { CrewTravelWindow } from "src/pages/CrewTravel/CrewTravelWindow";
import { HistoryWindow } from "src/pages/History/HistoryWindow";
import { ReportTreeViewWindow } from "src/pages/Reporting/ReportTreeViewWindow";
import { ReportingWindow } from "src/pages/Reporting/ReportingWindow";
import { FlightPairingStatusWindow } from "src/pages/FlightPairingStatus/FlightPairingStatusWindow";
import { FlightPairingStatusV2Window } from "src/pages/FlightPairingStatusV2/FlightPairingStatusV2Window";
import { ErrorsWindow } from "src/pages/Errors/ErrorsWindow";
import { AirportInfoWindow } from "src/pages/AirportInfo/AirportInfoWindow";
import { PairingSummaryWindow } from "src/pages/CrewSchedule/PairingSummaryWindow";
import { PairingCheckInStatusWindow } from "src/pages/PairingCheckInStatus/PairingCheckInStatusWindow";
import { CrewLedgerWindow } from "src/pages/CrewSchedule/CrewLedgerWindow";
import { APIVersionInfoWindow } from "src/pages/APIVersionInfo/APIVersionInfoWindow";
import { CrewPairingLedgerWindow } from "src/pages/CrewSchedule/CrewPairingLedger/CrewPairingLedgerWindow";
import { UnassignedFlightsWindow } from "src/pages/UnassignedFlightsWindow";
import { APIKeyWindow } from "src/pages/APIKey/APIVersionInfo/APIKeyWindow";
import { ChangePasswordWindow } from "src/pages/ChangePassword/ChangePasswordWindow";
import { PairingBuilderWindow } from "src/pages/PairingBuilder/PairingBuilderWindow";
import { PairingWaterfallWindow } from "src/pages/PairingWaterfall/PairingWaterfallWindow";
import { BidPackageEditorWindow } from "src/pages/BidManager/BidPackageEditorWindow";
import { BidPackageListWindow } from "src/pages/BidManager/BidPackageListWindow";
import { VacationBidPackageEditorWindow } from "src/pages/VacationBidManager/VacationBidPackageEditorWindow";
import { VacationBidPackageListWindow } from "src/pages/VacationBidManager/VacationBidPackageListWindow";
import { UserPreferencesWindow } from "src/pages/UserPreferences/UserPreferencesWindow";
import { FlightStatusWindow } from "src/pages/FlightStatus/FlightStatusWindow";
import { PDFViewerWindow } from "src/pages/PDFViewerWindow/PDFViewerWindow";
import { CSVViewerWindow } from "src/pages/CSVViewerWindow/CSVViewerWindow";
import { ImageViewerWindow } from "src/pages/ImageViewerWindow/ImageViewerWindow";
import { TextViewerWindow } from "src/pages/TextViewerWindow/TextViewerWindow";
import { EmailViewerWindow } from "src/pages/EmailViewerWindow/EmailViewerWindow";
import { OpsLinkWindow } from "src/pages/OpsLink/OpsLinkWindow";
import { DispatchDeskWindow } from "src/pages/DispatchDesk/DispatchDeskWindow";

export type ViewId =
  | "BetaEventEditor"
  | "FlightMovementMessage"
  | "UnassignedFlightsWindow"
  | "AdvancedSearchWindow"
  | "FlightBuilder"
  | "FlightTimeline"
  | "FlightSummary"
  | "AdminWindow"
  | "CrewScheduleWindow"
  | "PairingEditorWindow"
  | "ImportEventsWindow"
  | "NFAEditorWindow"
  | "ManageContentWindow"
  | "ReviewContentWindow"
  | "ContentListWindow"
  | "CrewTravelWindow"
  | "HistoryWindow"
  | "ReportingWindow"
  | "ReportTreeViewWindow"
  | "FlightPairingStatusWindow"
  | "FlightPairingStatusV2Window"
  | "ErrorsWindow"
  | "AirportInfoWindow"
  | "PairingSummaryWindow"
  | "PairingCheckInStatusWindow"
  | "CrewLedgerWindow"
  | "APIVersionInfoWindow"
  | "APIKeyWindow"
  | "ChangePasswordWindow"
  | "CrewPairingLedgerWindow"
  | "PairingWaterfallWindow"
  | "PairingBuilderWindow"
  | "BidPackageEditorWindow"
  | "BidPackageListWindow"
  | "VacationBidPackageEditorWindow"
  | "VacationBidPackageListWindow"
  | "UserPreferencesWindow"
  | "FlightStatusWindow"
  | "PDFViewerWindow"
  | "CSVViewerWindow"
  | "ImageViewerWindow"
  | "TextViewerWindow"
  | "EmailViewerWindow"
  | "OpsLinkWindow"
  | "DispatchDeskWindow";

interface IMosaicComponent {
  component: React.Component;
  title: string;
}

type IMosaicMap = {
  [key in ViewId]: typeof DashboardWindow | ((props: any) => JSX.Element);
};

export const COMPONENT_MAP: IMosaicMap = {
  BetaEventEditor,
  FlightMovementMessage: MovementMessages,
  FlightBuilder,
  FlightTimeline: FlightTimelineWrapper,
  AdvancedSearchWindow: AdvancedSearch,
  FlightSummary,
  AdminWindow,
  CrewScheduleWindow,
  PairingEditorWindow,
  ImportEventsWindow,
  NFAEditorWindow,
  ManageContentWindow,
  ReviewContentWindow,
  ContentListWindow,
  CrewTravelWindow,
  HistoryWindow,
  ReportingWindow,
  ReportTreeViewWindow,
  FlightPairingStatusWindow,
  FlightPairingStatusV2Window,
  ErrorsWindow,
  AirportInfoWindow,
  PairingSummaryWindow,
  PairingCheckInStatusWindow,
  CrewLedgerWindow,
  APIVersionInfoWindow,
  APIKeyWindow,
  ChangePasswordWindow,
  CrewPairingLedgerWindow,
  UnassignedFlightsWindow,
  PairingWaterfallWindow,
  PairingBuilderWindow,
  BidPackageEditorWindow,
  BidPackageListWindow,
  VacationBidPackageEditorWindow,
  VacationBidPackageListWindow,
  UserPreferencesWindow,
  FlightStatusWindow,
  PDFViewerWindow,
  CSVViewerWindow,
  ImageViewerWindow,
  TextViewerWindow,
  EmailViewerWindow,
  OpsLinkWindow,
  DispatchDeskWindow,
};

// DEFINE STATES
export interface IMosaicManager {
  currentNode: IMosaicNode;
}

const defaultWindowState: ILeafNode = {
  id: v1(),
  tabNodes: [],
  activeIndex: -1,
};

// Debug.log(mosaicLayout)
const initialState: IMosaicManager = {
  currentNode: defaultWindowState,
  // currentNode: defaultWindowState
};

// Convert leaf node to array of it's tab node component names.
const getNodeNames = (mosaicLayout: ILeafNode): string[] => {
  return mosaicLayout?.tabNodes?.length > 0 ? mosaicLayout?.tabNodes?.map((node) => node.componentName) : [];
};

/**
 * Will check a desired layout and return true if all windows defined exist in the component map.
 * @param desiredLayout - layout you wish to apply to the mosaic component.
 * @param COMPONENT_MAP - Current available windows defined by the application.
 * @returns
 */
const layoutSafeToLoad = (desiredLayout: IMosaicNode, COMPONENT_MAP: IMosaicMap): boolean => {
  if (desiredLayout && COMPONENT_MAP) {
    let componentNameArray: string[] = [];
    if (desiredLayout.hasOwnProperty("tabNodes")) {
      // LEAF NODE
      componentNameArray = [...componentNameArray, ...getNodeNames(desiredLayout as ILeafNode)];
    }
    if (desiredLayout.hasOwnProperty("first")) {
      // PARENT NODE
      const firstLeafNode = (desiredLayout as IParentNode).first;
      componentNameArray = [...componentNameArray, ...getNodeNames(firstLeafNode as ILeafNode)];
    }
    if (desiredLayout.hasOwnProperty("second")) {
      // PARENT NODE
      const secondLeafNode = (desiredLayout as IParentNode).second;
      componentNameArray = [...componentNameArray, ...getNodeNames(secondLeafNode as ILeafNode)];
    }
    let i;
    for (i = 0; i < componentNameArray.length; i++) {
      // If componentName cannot be found return false
      if (!COMPONENT_MAP.hasOwnProperty(componentNameArray[i]) && !isCustomerViewId(componentNameArray[i])) {
        return false;
      }
    }
    return true;
  }
  return false;
};

// REDUCER
export const MosaicManagerReducer = (
  state: IMosaicManager = initialState,
  action: MosaicManagerActions.Action
): IMosaicManager => {
  switch (action.type) {
    case "MOVE_TAB_NODE":
    case "REMOVE_TAB_NODE":
    case "APPEND_TAB_NODE":
    case "SET_ACTIVE_INDEX":
    case "MOVE_TAB_NODE_TO_END":
    case "UPDATE_TAB_NODE": {
      const currentNode =
        !isEmpty(action.currentNode) && layoutSafeToLoad(action.currentNode, COMPONENT_MAP)
          ? { ...action.currentNode }
          : defaultWindowState;
      return { ...state, currentNode };
    }
    case "SET_DEFAULT_NODE":
      if (!isEmpty(action.currentNode) && layoutSafeToLoad(action.currentNode, COMPONENT_MAP)) {
        return { ...state, currentNode: action.currentNode };
      }
      return state;
    case "UPDATE_SPLIT_VIEW_PERCENTAGE":
      return { ...state, currentNode: action.currentNode };
    default:
      return state;
  }
};
